import type { ComponentProps } from "react";
import { RoutePaths } from "@/core/constants/RoutePaths";
import type { MainLayout as MainLayoutComponent } from "@/layouts/MainLayout";
import { lazyWithPreload } from "@/shared/utils/lazyWithPreload";

/**
 * Páginas públicas de conteúdo, abertas por quem não tem conta (link de
 * marketing, documento legal, docs da API). Nelas o pré-carregamento do shell
 * autenticado no idle (~300 kB comprimidos) só disputa banda com a própria
 * página, que ainda baixa o chunk dela (SA-1474).
 */
const PUBLIC_CONTENT_PATHS: ReadonlySet<string> = new Set([
  RoutePaths.PLAN_CALCULATOR,
  RoutePaths.PRIVACY_POLICY,
  RoutePaths.TERMS_OF_USE,
  RoutePaths.DOCS,
]);

/** Se a página de entrada deve pré-carregar o shell autenticado no idle. */
export function shouldPrefetchShell(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return !PUBLIC_CONTENT_PATHS.has(normalized);
}

/**
 * O que o idle do `AppRoutes` pré-carrega além do shell (SA-1474).
 *
 * - Onboarding: só sem sessão guardada (login, cadastro); com sessão o boot já
 *   baixou o shell, e o onboarding disputava banda com a lista de conversas.
 * - Conversa aberta: com sessão, fora de Conversas. Uma notificação ou um link
 *   leva direto a `/chats/:id` sem F5, e sem isso a página e a conversa
 *   baixariam em série. Em Conversas quem decide é a lista (intenção ou 2,5 s,
 *   ver `pages/Chats/deferredPanelChunks`).
 */
export function idlePreloadsFor(pathname: string, hasSession: boolean) {
  const onChats =
    pathname === RoutePaths.CHATS ||
    pathname.startsWith(`${RoutePaths.CHATS}/`);
  return {
    onboarding: !hasSession,
    openConversation: hasSession && !onChats,
  };
}

/**
 * Os três componentes lazy do caminho do F5 até a tela de conversas (SA-1452).
 * Moram aqui, e não no `AppRoutes`, para o boot pré-carregar os MESMOS objetos
 * que as rotas renderizam (ver `lazyWithPreload`).
 */
export const AuthenticatedAppProviders = lazyWithPreload(
  () => import("@/core/providers/AuthenticatedAppProviders")
);

export const MainLayout = lazyWithPreload<
  ComponentProps<typeof MainLayoutComponent>
>(() =>
  import("@/layouts/MainLayout").then((module) => ({
    default: module.MainLayout,
  }))
);

export const ChatsPage = lazyWithPreload(() => import("@/pages/Chats"));

/**
 * A conversa aberta (timeline, composer, detalhe do contato), fora do chunk da
 * lista (SA-1474). O boot a pré-carrega quando a URL já é `/chats/:id`; na lista,
 * ela baixa no idle depois que as linhas aparecem (`pages/Chats`).
 */
export const OpenConversation = lazyWithPreload(
  () => import("@/features/chat/components/ChatBody/OpenConversation")
);
